import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, ChevronRight, ChevronLeft, Users, User, CalendarDays, Save, Eye, AlertCircle } from 'lucide-react';
import RegPhotoField from './RegPhotoField';
import { regCfg, loadGuest, saveGuest, getGuest } from './regSession';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FAM = `${API_URL}/api/reg-family`;
const DEFAULT_BRAND = '#b45309';

// "2026-10-01" / "2026-10-01T00:00" → "Oct 1, 2026" (no timezone shift).
export const fmtDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  if (!m) return v ? String(v) : '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const absUrl = (u) => (!u ? '' : /^(https?:|data:)/.test(u) ? u : `${API_URL}${u}`);
const empty = (v) => v === undefined || v === null || v === '' || v === false || (Array.isArray(v) && v.length === 0);
// Club messages may be formatted (HTML) or plain text typed in the builder.
const msgCls = (t) => (/<[a-z][^>]*>/i.test(String(t || '')) ? '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5' : 'whitespace-pre-line');
// Plain-text club messages (often pasted from email): **bold**, "—"/"-"/"•" lines →
// bullets, "TITLE:" lines → headings, blank lines → paragraphs. HTML is left alone.
const escapeHtml = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export const formatMessage = (t) => {
  const src = String(t || '');
  if (/<[a-z][^>]*>/i.test(src)) return src;
  const inline = (x) => escapeHtml(x).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>').replace(/^\*\*\s*(.+)$/, '<b>$1</b>');
  const out = []; let list = null;
  const flush = () => { if (list) { out.push(`<ul>${list.join('')}</ul>`); list = null; } };
  src.split(/\r?\n/).forEach(raw => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    const b = /^(?:[—–•·]|-(?!-)|\*(?!\*))\s*(.+)$/.exec(line);
    if (b) { (list = list || []).push(`<li>${inline(b[1])}</li>`); return; }
    flush();
    if (/^[A-Z0-9][^.!?]{2,58}:$/.test(line)) { out.push(`<h4>${escapeHtml(line.replace(/:$/, ''))}</h4>`); return; }
    out.push(`<p>${inline(line)}</p>`);
  });
  flush();
  return out.join('');
};
const blankFamily = () => ({ father: {}, mother: {}, address: {}, answers: {} });

// Family-facing renderer for the BUILT reg_forms (docs/REGISTRATION_FAMILY_EXPERIENCE.md):
// club header (logo, name, colours) · sign in / create account from the link · autosave +
// resume · one column on phones · photo questions with camera · inline required checks.
// `preview` = {form, brand} from the builder: same screens, nothing is saved or sent.
export default function RegistrationFormPanel({ orgId, publicId, preview = null }) {
  const [loading, setLoading] = useState(!preview);
  const [loadErr, setLoadErr] = useState('');
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState(preview?.form || null);
  const [landing, setLanding] = useState(null);
  const [brandInfo, setBrandInfo] = useState(preview?.brand || null);
  const [me, setMe] = useState(preview ? { id: 'preview', email: 'family@example.com', first_name: 'Pat', last_name: 'Family' } : null);
  const [step, setStep] = useState('landing'); // landing | auth | resume | family | players | review | done
  const [family, setFamily] = useState(blankFamily());
  const [players, setPlayers] = useState([{ answers: {} }]);
  const [cur, setCur] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [result, setResult] = useState(null);
  const [payOption, setPayOption] = useState('full');
  const [coupon, setCoupon] = useState('');
  const [couponMsg, setCouponMsg] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [draft, setDraft] = useState(null);          // saved draft offered on resume
  const [savedAt, setSavedAt] = useState('');
  const [laterMsg, setLaterMsg] = useState('');
  const [returnStep, setReturnStep] = useState('');   // where to go back to after a re-sign-in
  const [guest, setGuestState] = useState('');        // guest key (no login) for this form
  const [authNote, setAuthNote] = useState('');       // why we ask for a login (paying now)
  const [laterAsk, setLaterAsk] = useState(false);    // guest without a parent email → ask where to send the link
  const [laterEmail, setLaterEmail] = useState('');
  const [showAll, setShowAll] = useState(false);      // welcome message: read more
  const submittingRef = useRef(false);                // no autosave once Submit starts
  const round2 = (n) => Math.round(n * 100) / 100;
  const topRef = useRef(null);

  // Builder preview: follow the AD's edits live.
  useEffect(() => { if (preview) { setForm(preview.form); setBrandInfo(preview.brand); } }, [preview]);

  const load = useCallback(async () => {
    if (preview || !orgId) return;
    try {
      if (publicId) {
        const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/public/by-id/${publicId}`);
        setForm(data.form); setLanding(data.landing); setBrandInfo(data.brand);
      } else {
        const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/public/forms`);
        setForms(data.forms || []); setBrandInfo(data.brand);
        if ((data.forms || []).length === 1) {
          const f = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/public/forms/${data.forms[0].id}`);
          setForm(f.data.form); setLanding(f.data.landing);
        }
      }
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || 'This registration could not be loaded.');
      // Still show the club's look on a "not open" page.
      try { const b = await axios.get(`${FAM}/${orgId}/brand`); setBrandInfo(b.data.brand); } catch { /* neutral */ }
    }
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      // Only a FAMILY login of this club registers as a member; staff browsing the
      // public link register as a guest like anyone else.
      const fam = (data.roles || []).some(r => ['family', 'parent', 'guardian'].includes(r.role_type) && r.org_id === orgId);
      setMe(fam ? data : null);
    } catch { setMe(null); }
    setLoading(false);
  }, [orgId, publicId, preview]);
  useEffect(() => { load(); }, [load]);

  const setGuest = (t) => { saveGuest(form?.id, t); setGuestState(t || ''); };
  // A "Finish later" link (?resume=…) or this browser's guest key → saved progress.
  useEffect(() => {
    if (preview || !form?.id || loading) return;
    const code = new URLSearchParams(window.location.search).get('resume');
    if (code) {
      axios.post(`${FAM}/${orgId}/forms/${form.id}/resume`, { code }, { withCredentials: true })
        .then(r => { setGuest(r.data.guest); setDraft(r.data.draft || null); })
        .catch(e => setLaterMsg(e?.response?.data?.detail || 'This link has expired — start the registration again.'))
        .finally(() => { try { window.history.replaceState({}, '', window.location.pathname); } catch { /* ignore */ } });
      return;
    }
    if (!me) setGuestState(loadGuest(form.id));
  }, [form?.id, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  // Look for saved progress so the landing can say "Continue".
  useEffect(() => {
    if (preview || !form?.id || !(me || guest)) return;
    axios.get(`${FAM}/${orgId}/forms/${form.id}/draft`, regCfg())
      .then(r => setDraft(r.data.draft || null))
      .catch(e => { setDraft(null); if (e?.response?.status === 401 && !me) setGuest(''); });
  }, [me, guest, form?.id, orgId, preview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave (signed-in family, after the landing).
  useEffect(() => {
    if (preview || !(me || guest) || !form?.id || !['family', 'players', 'review'].includes(step) || submittingRef.current) return undefined;
    const t = setTimeout(async () => {
      if (submittingRef.current) return;
      try {
        const { data } = await axios.put(`${FAM}/${orgId}/forms/${form.id}/draft`,
          { state: { step, cur, family, players, payOption } }, regCfg());
        setSavedAt(data.saved_at);
      } catch { /* next change retries */ }
    }, 1200);
    return () => clearTimeout(t);
  }, [family, players, step, cur, payOption, me, guest, form?.id, orgId, preview]);

  const pickForm = async (fid) => {
    const { data } = await axios.get(`${API_URL}/api/reg-submissions/${orgId}/public/forms/${fid}`);
    setForm(data.form); setLanding(data.landing); if (data.brand) setBrandInfo(data.brand);
  };

  const go = (s) => { setStep(s); setErrors({}); setTimeout(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); };

  const firstStep = form?.staff_only ? 'players' : 'family';
  const afterSignIn = async (user) => {
    setMe(user);
    if (preview) { go(firstStep); return; }
    try { await axios.post(`${FAM}/${orgId}/forms/${form.id}/join`, {}, { withCredentials: true }); } catch { /* submit re-checks */ }
    if (getGuest()) {   // they started as a guest: move the saved answers + photos to the login
      try { await axios.post(`${FAM}/${orgId}/forms/${form.id}/claim-guest`, {}, regCfg()); } catch { /* keep going */ }
      setGuest('');
    }
    if (returnStep) { const r = returnStep; setReturnStep(''); go(r); return; }   // answers are still on screen
    let d = null;
    try { d = (await axios.get(`${FAM}/${orgId}/forms/${form.id}/draft`, regCfg())).data.draft; } catch { d = null; }
    if (d) { setDraft(d); go('resume'); return; }
    if (!form.staff_only) setFamily(f => (f.father?.name || f.mother?.name) ? f : { ...f, father: { name: `${user.first_name || ''} ${user.last_name || ''}`.trim(), email: user.email, phone: user.phone || '' } });
    go(firstStep);
  };
  const resume = () => {
    const s = draft?.state || {};
    setFamily({ ...blankFamily(), ...(s.family || {}) });
    setPlayers((s.players && s.players.length) ? s.players : [{ answers: {} }]);
    setCur(Math.min(s.cur || 0, Math.max((s.players || []).length - 1, 0)));
    setPayOption(s.payOption || 'full');
    const st = ['family', 'players', 'review'].includes(s.step) ? s.step : firstStep;
    go(form.staff_only && st === 'family' ? 'players' : st);
  };
  // No login needed: a guest key for this form (kept in this browser).
  const ensureGuest = async () => {
    if (getGuest()) return;
    const { data } = await axios.post(`${FAM}/${orgId}/forms/${form.id}/guest`, {}, { withCredentials: true });
    setGuest(data.guest);
  };
  const start = async () => {
    if (preview) { go(firstStep); return; }
    if (me) { afterSignIn(me); return; }
    try { await ensureGuest(); } catch (e) { setLaterMsg(e?.response?.data?.detail || 'Could not start — try again.'); return; }
    if (draft) { go('resume'); return; }
    go(firstStep);
  };
  const startOver = async () => {
    try { await axios.delete(`${FAM}/${orgId}/forms/${form.id}/draft`, regCfg()); } catch { /* ignore */ }
    setDraft(null); setFamily(blankFamily()); setPlayers([{ answers: {} }]); setCur(0);
    if (me) afterSignIn(me); else go(firstStep);
  };
  const finishLater = async (emailOverride) => {
    if (preview) { setLaterMsg('Preview — families get an email with a link back to this point.'); return; }
    const to = me ? me.email : (emailOverride || ['father', 'mother'].map(k => family[k]?.email).find(Boolean) || '');
    if (!me && !to) { setLaterAsk(true); return; }
    try {
      await axios.put(`${FAM}/${orgId}/forms/${form.id}/draft`, { state: { step, cur, family, players, payOption } }, regCfg());
      const { data } = await axios.post(`${FAM}/${orgId}/forms/${form.id}/draft/email-link`, { email: to }, regCfg());
      setLaterAsk(false);
      setLaterMsg(data.status === 'recently_sent' ? `We already emailed ${data.to} a link a few minutes ago — check your inbox (and spam).`
        : me ? `Saved. We emailed ${data.to} a link — sign in with that email to pick up where you left off.`
          : `Saved. We emailed ${data.to} a link back to this registration — it works for 14 days.`);
    } catch (e) { setLaterMsg(e?.response?.data?.detail || 'Could not save just now — try again in a moment.'); }
  };

  const allQuestions = () => {
    const qs = [...(form?.questions || [])];
    (form?.sections || []).forEach(s => (s.questions || []).forEach(q => qs.push({ ...q, section: s.name })));
    return qs;
  };
  // R8 — conditions joined by AND (all) or OR (any); a condition matches any of its values.
  const visible = (q, ans) => {
    const conds = (q.logic || {}).conditions || [];
    if (!conds.length) return true;
    const hit = (c) => {
      let v = ans[c.source_id];
      if (v === undefined && (allQuestions().find(x => x.id === c.source_id) || {}).qtype === 'checkbox') v = false;
      const want = Array.isArray(c.value) ? c.value : [c.value];
      return Array.isArray(v) ? v.some(x => want.includes(x)) : want.includes(v);
    };
    return (q.logic.join === 'or') ? conds.some(hit) : conds.every(hit);
  };
  const feeLabel = (f) => (f && typeof f === 'object' ? ('min' in f ? ` ($${f.min}–$${f.max}, you choose)` : ` (+$${f.amount} each, up to ${f.max_qty})`) : (f ? ` (+$${f})` : ''));
  const setPA = (qid, val) => setPlayers(p => { const c = [...p]; c[cur] = { ...c[cur], answers: { ...c[cur].answers, [qid]: val } }; return c; });
  const setP = (patch) => setPlayers(p => { const c = [...p]; c[cur] = { ...c[cur], ...patch }; return c; });
  // Birth-certificate reader: what the photo says, kept on the player (not an answer).
  const [reading, setReading] = useState('');
  const readDob = async (q, val, idx) => {
    if (preview || !val || !q.read_dob) return;
    setReading(q.id);
    try {
      const { data } = await axios.post(`${FAM}/${orgId}/forms/${form.id}/read-dob`, { question_id: q.id, answer: val }, regCfg());
      setPlayers(pl => { const c = [...pl]; const cur0 = c[idx] || { answers: {} }; c[idx] = { ...cur0, dob_reads: { ...(cur0.dob_reads || {}), [q.id]: data.read } }; return c; });
    } catch { /* the club checks the document by hand */ }
    finally { setReading(''); }
  };
  const clearDobRead = (qid, idx) => setPlayers(pl => { const c = [...pl]; const r = { ...((c[idx] || {}).dob_reads || {}) }; delete r[qid]; c[idx] = { ...c[idx], dob_reads: r }; return c; });
  const setFam = (k, patch) => setFamily(f => ({ ...f, [k]: { ...(f[k] || {}), ...patch } }));

  // Inline required checks — the family sees exactly what is missing.
  const missingQs = (qs, ans) => qs.filter(q => q.required && visible(q, ans) && empty(ans[q.id])).map(q => q.id);
  const checkFamily = () => {
    const e = {};
    if (!form.staff_only) {
      const ok = ['father', 'mother'].some(k => family[k]?.name && family[k]?.email && family[k]?.phone);
      if (!ok) e.parent = 'Enter at least one parent with name, phone and email';
    }
    missingQs(allQuestions().filter(q => q.scope === 'family'), family.answers || {}).forEach(id => { e[id] = 'Required'; });
    return e;
  };
  const checkPlayer = (p) => {
    const e = {};
    if (!p.first_name) e.first_name = 'Required';
    if (!p.last_name) e.last_name = 'Required';
    if (!p.birthday) e.birthday = 'Required';
    if (!p.gender) e.gender = 'Required';
    if (form.staff_only && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p.email || '')) e.email = 'Enter your email';
    if ((form.groups || []).some(g => g.enabled !== false) && !p.group_id) e.group_id = 'Pick one';
    missingQs(allQuestions().filter(q => q.scope !== 'family'), p.answers || {}).forEach(id => { e[id] = 'Required'; });
    (form.agreements || []).filter(a => a.enabled !== false).forEach(a => { if (empty((p.answers || {})[`agreement_${a.id}`])) e[`agreement_${a.id}`] = 'Required'; });
    return e;
  };
  const guard = (e, next) => {
    if (Object.keys(e).length) {
      setErrors(e);
      setTimeout(() => document.querySelector('[data-err="1"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
      return;
    }
    next();
  };

  const submit = async (payment, retried = false) => {
    if (preview) { setSubmitErr('Preview — nothing is submitted.'); return; }
    submittingRef.current = true;
    setSubmitting(true); setSubmitErr('');
    try {
      const { data } = await axios.post(`${API_URL}/api/reg-submissions/${orgId}/public/forms/${form.id}/submit`,
        { family, players, payment, origin_url: window.location.origin }, regCfg());
      // The registration is saved at this point — a payment hiccup must never
      // send the family back to the form (they would register twice).
      if (data.pay?.payment_id) {
        try {
          const { data: pd } = me
            ? await axios.post(`${API_URL}/api/payments/${data.pay.payment_id}/pay`,
              { origin_url: window.location.origin, installment_number: data.pay.installment_number }, { withCredentials: true })
            : await axios.post(`${API_URL}/api/payments/public/${data.pay.pay_token}/pay`,
              { origin_url: window.location.origin, installment_number: data.pay.installment_number });
          if (pd.url) { window.location.href = pd.url; return; }
        } catch { data.pay_pending = true; }
      }
      setResult(data); go('done');
    } catch (e) {
      submittingRef.current = false;
      if (e?.response?.status === 401) {
        if (e?.response?.data?.detail === 'LOGIN_TO_PAY') { setAuthNote('This registration takes a payment now, so it needs a family login. Your answers are kept.'); setReturnStep('review'); go('auth'); return; }
        if (me) { setMe(null); setReturnStep('review'); go('auth'); return; }
        if (!retried) { setGuest(''); try { await ensureGuest(); submittingRef.current = false; setSubmitting(false); return submit(payment, true); } catch { /* fall through */ } }
      }
      setSubmitErr(e?.response?.data?.detail || 'Submit failed — your answers are saved, try again.');
    } finally { setSubmitting(false); }
  };

  const brand = brandInfo?.primary_color || DEFAULT_BRAND;
  const inp = (bad) => `w-full rounded-lg border px-3 h-11 text-base sm:text-sm bg-white dark:bg-slate-700 dark:border-slate-600 ${bad ? 'border-red-500 ring-1 ring-red-500' : 'border-slate-300'}`;
  const errMsg = (k) => (errors[k] ? <p className="text-xs text-red-600 mt-1" data-err="1">{errors[k]}</p> : null);
  const primaryBtn = 'inline-flex items-center justify-center gap-1 px-5 h-11 rounded-lg text-white font-semibold disabled:opacity-60';
  const ghostBtn = 'inline-flex items-center justify-center gap-1 px-3 h-11 rounded-lg text-slate-600 dark:text-slate-300';

  const header = () => (
    <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-white" style={{ background: brand }} data-testid="reg-brand-header">
      {brandInfo?.logo_url && <img src={absUrl(brandInfo.logo_url)} alt="" className="h-11 w-11 rounded bg-white/90 object-contain p-0.5" />}
      <div className="min-w-0">
        <div className="font-bold leading-tight truncate" data-testid="reg-brand-name">{brandInfo?.org_name || 'Registration'}</div>
        {brandInfo?.tagline && <div className="text-xs opacity-90 truncate">{brandInfo.tagline}</div>}
      </div>
      {preview && <span className="ml-auto text-[11px] bg-white/20 rounded px-2 py-0.5 flex items-center gap-1"><Eye className="h-3 w-3" /> Preview</span>}
    </div>
  );
  const stepsFor = form?.staff_only ? [['players', 'You'], ['review', 'Review']] : [['family', 'Parents'], ['players', 'Players'], ['review', 'Review & pay']];
  const progress = () => {
    const idx = stepsFor.findIndex(([k]) => k === step);
    if (idx < 0) return null;
    return (
      <div className="flex gap-1" data-testid="reg-progress">
        {stepsFor.map(([k, l], i) => (
          <div key={k} className="flex-1">
            <div className="h-1.5 rounded-full" style={{ background: i <= idx ? brand : '#cbd5e1' }} />
            <div className={`text-[11px] mt-1 ${i === idx ? 'font-semibold' : 'text-slate-500'}`}>{l}</div>
          </div>
        ))}
      </div>
    );
  };
  const saveBar = () => (preview || !(me || guest) ? null : (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <span data-testid="reg-saved">{savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Your answers save as you go'}</span>
      <button className="underline flex items-center gap-1" onClick={() => finishLater()} data-testid="reg-finish-later"><Save className="h-3 w-3" /> Finish later</button>
    </div>
  ));

  const shell = (children) => (
    <div className="max-w-2xl mx-auto space-y-3" ref={topRef}>
      {header()}
      {children}
    </div>
  );

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (!form) {
    return (
      shell(<>
        <div className="space-y-3" data-testid="reg-form-picker">
          {publicId ? (
            <div className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700 p-6 text-center space-y-1" data-testid="reg-not-open">
              <h2 className="text-lg font-bold">This registration isn't open right now</h2>
              <p className="text-sm text-slate-500">It may not have opened yet, or it has closed. Please check back, or contact the club.</p>
            </div>
          ) : (<>
            <h2 className="text-xl font-bold">Open registrations</h2>
            {(loadErr || forms.length === 0) && <p className="text-slate-500">No open registrations right now.</p>}
          </>)}
          {forms.map(f => (
            <button key={f.id} onClick={() => pickForm(f.id)} data-testid={`reg-open-${f.id}`}
              className="w-full text-left p-4 rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700">
              <div className="font-semibold">{f.name}</div>
              <div className="text-xs text-slate-500">{f.program?.sport_name} · {f.program?.season}{f.close_date ? ` · closes ${fmtDate(f.close_date)}` : ''}{f.landing?.age_min != null ? ` · ages ${f.landing.age_min}–${f.landing.age_max}` : ''}</div>
              {f.display_status && f.display_status !== 'open' && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`reg-badge-${f.id}`}>{f.display_status.replace('_', ' ')}</span>}
            </button>
          ))}
        </div>
      </>)
    );
  }

  // One question, any type.
  const renderField = (q, ans, setAns, forLabel) => {
    if (!visible(q, ans)) return null;
    const v = ans[q.id] ?? (q.allow_multiple ? [] : '');
    const bad = !!errors[q.id];
    return (
      <div key={q.id} className="space-y-1" data-testid={`field-${q.id}`}>
        <label className="block text-sm font-medium">{q.label}{q.required && <span className="text-red-500 ml-1" title="Required">*</span>}</label>
        {q.help_text && <p className="text-xs text-slate-500">{q.help_text}</p>}
        {['text', 'email', 'phone', 'number', 'date'].includes(q.qtype) && (
          <input type={q.qtype === 'phone' ? 'tel' : q.qtype} value={v} onChange={e => setAns(q.id, e.target.value)} className={inp(bad)} data-testid={`input-${q.id}`} />
        )}
        {q.qtype === 'textarea' && <textarea rows={3} value={v} onChange={e => setAns(q.id, e.target.value)} className={`${inp(bad)} h-auto py-2`} data-testid={`input-${q.id}`} />}
        {['select', 'dropdown', 'multiple_choice'].includes(q.qtype) && (
          q.allow_multiple ? (
            <div className="space-y-1">
              {(q.options || []).map(o => (
                <label key={o} className="flex items-center gap-3 text-sm min-h-[40px]">
                  <input type="checkbox" className="h-5 w-5" checked={(v || []).includes(o)} onChange={e => {
                    const set = new Set(v || []); e.target.checked ? set.add(o) : set.delete(o); setAns(q.id, [...set]);
                  }} />{o}{feeLabel(q.response_fees?.[o])}
                </label>
              ))}
            </div>
          ) : (
            <select value={v} onChange={e => setAns(q.id, e.target.value)} className={inp(bad)} data-testid={`input-${q.id}`}>
              <option value="">Choose…</option>
              {(q.options || []).map(o => <option key={o} value={o}>{o}{feeLabel(q.response_fees?.[o])}</option>)}
            </select>
          )
        )}
        {(() => {
          // R9 — payer-chosen amount (donation) or quantity for the picked answer.
          const picks = Array.isArray(v) ? v : [v];
          const spec = picks.map(x => q.response_fees?.[x]).find(f => f && typeof f === 'object');
          if (!spec) return null;
          if ('min' in spec) return (
            <input type="number" min={spec.min} max={spec.max} step="1" placeholder={`Amount ($${spec.min}–$${spec.max})`} value={ans[`${q.id}__amount`] ?? ''}
              onChange={e => setAns(`${q.id}__amount`, e.target.value)} className={`${inp(false)} sm:w-56`} data-testid={`amount-${q.id}`} />);
          return (
            <select value={ans[`${q.id}__qty`] ?? 1} onChange={e => setAns(`${q.id}__qty`, e.target.value)} className={`${inp(false)} sm:w-40`} data-testid={`qty-${q.id}`}>
              {Array.from({ length: (spec.max_qty || 1) + 1 }).map((_, n) => <option key={n} value={n}>Qty {n}</option>)}
            </select>);
        })()}
        {q.qtype === 'file' && (
          <RegPhotoField orgId={orgId} formId={form.id} question={q} value={v} brand={brand} preview={!!preview} forLabel={forLabel}
            onChange={val => { setAns(q.id, val); if (q.scope !== 'family') { if (val) readDob(q, val, cur); else clearDobRead(q.id, cur); } }} />
        )}
        {q.qtype === 'file' && q.read_dob && q.scope !== 'family' && dobNote(q)}
        {q.qtype === 'checkbox' && <label className="flex items-center gap-3 text-sm min-h-[40px]"><input type="checkbox" className="h-5 w-5" checked={v === true || v === 'true'} onChange={e => setAns(q.id, e.target.checked)} data-testid={`input-${q.id}`} />{q.options?.[0] || 'Yes'}</label>}
        {errMsg(q.id)}
      </div>
    );
  };
  // What the birth certificate says vs the date of birth typed for this player.
  const dobNote = (q) => {
    if (preview) return <p className="text-xs text-slate-500">On the live form the date of birth is read from the photo and checked against the one entered.</p>;
    if (reading === q.id) return <p className="text-xs text-slate-500 flex items-center gap-1" data-testid={`dob-reading-${q.id}`}><Loader2 className="h-3 w-3 animate-spin" />Reading the date of birth…</p>;
    const pl = players[cur] || {}; const r = (pl.dob_reads || {})[q.id];
    if (!r || !(pl.answers || {})[q.id]) return null;
    const box = 'rounded-lg border px-3 py-2 text-sm';
    if (!r.dob) return <p className={`${box} bg-slate-50 border-slate-200 text-slate-600 dark:bg-slate-700/40 dark:border-slate-600 dark:text-slate-300`} data-testid={`dob-note-${q.id}`}>We couldn't read a date of birth from this photo. A clear, flat photo helps — or continue and the club will check it.</p>;
    const use = <button type="button" className="underline font-medium ml-1" onClick={() => setP({ birthday: r.dob })} data-testid={`dob-use-${q.id}`}>Use this date</button>;
    if (!pl.birthday) return <p className={`${box} bg-sky-50 border-sky-200 text-sky-800 dark:bg-sky-900/20 dark:border-sky-800 dark:text-sky-300`} data-testid={`dob-note-${q.id}`}>The certificate shows <b>{fmtDate(r.dob)}</b>{r.name ? ` for ${r.name}` : ''}.{use}</p>;
    if (pl.birthday === r.dob) return <p className={`${box} bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300`} data-testid={`dob-note-${q.id}`}><CheckCircle2 className="h-4 w-4 inline mr-1" />Matches the date of birth you entered.</p>;
    return <p className={`${box} bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300`} data-testid={`dob-note-${q.id}`}>The certificate shows <b>{fmtDate(r.dob)}</b>, but you entered <b>{fmtDate(pl.birthday)}</b>.<button type="button" className="underline font-medium ml-1" onClick={() => setP({ birthday: r.dob })} data-testid={`dob-use-${q.id}`}>Use the certificate date</button></p>;
  };
  // Section header before the first visible question of each named section.
  const withSections = (qs, ans, setAns, forLabel) => {
    let last = null;
    return qs.map(q => {
      const el = renderField(q, ans, setAns, forLabel);
      if (!el) return null;
      const head = q.section && q.section !== last ? <h4 key={`sec-${q.id}`} className="text-sm font-semibold pt-3 border-t dark:border-slate-700">{q.section}</h4> : null;
      if (q.section) last = q.section;
      return head ? <React.Fragment key={`f-${q.id}`}>{head}{el}</React.Fragment> : el;
    });
  };
  const bornRange = (g) => (g.dob_from || g.dob_to) ? `Born ${[fmtDate(g.dob_from), fmtDate(g.dob_to)].filter(Boolean).join(' – ')}` : '';

  // LANDING
  if (step === 'landing') {
    const groups = (form.groups || []).filter(g => g.enabled !== false);
    const lp = landing || { program: form.program, open_date: form.open_date, close_date: form.close_date };
    const msg = formatMessage(form.welcome_message);
    const long = String(form.welcome_message || '').length > 700;
    const startBtn = (
      <button className={`${primaryBtn} w-full sm:w-auto`} style={{ background: brand }} data-testid="start-registration" onClick={start}>
        {draft ? 'Continue your registration' : 'Start registration'} <ChevronRight className="h-4 w-4" />
      </button>
    );
    return (
      shell(<>
        <div className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700 overflow-hidden" data-testid="reg-landing">
          <div className="p-5 sm:p-6 space-y-3">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: brand }}>{lp?.program?.sport_name} · {lp?.program?.season}</div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{form.name}</h1>
            <div className="flex flex-wrap gap-2 text-xs">
              {(lp?.open_date || lp?.close_date) && <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 bg-slate-100 dark:bg-slate-700"><CalendarDays className="h-3.5 w-3.5" />{lp.close_date ? `Register by ${fmtDate(lp.close_date)}` : `Opens ${fmtDate(lp.open_date)}`}</span>}
              {lp?.age_min != null && <span className="rounded-full px-3 py-1 bg-slate-100 dark:bg-slate-700">Ages {lp.age_min}–{lp.age_max}</span>}
              {groups.length > 0 && groups.every(g => !g.fee) && <span className="rounded-full px-3 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Free to register</span>}
            </div>
            {draft && <p className="text-sm rounded-lg px-3 py-2 bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-300">You started this on {fmtDate(draft.updated_at)} — continue where you left off.</p>}
            <div className="hidden sm:block">{startBtn}</div>
          </div>
          {groups.length > 0 && (
            <div className="px-5 sm:px-6 pb-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Choose from</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {groups.map(g => (
                  <div key={g.id} className="rounded-lg border p-3 dark:border-slate-700" style={{ borderLeft: `4px solid ${brand}` }}>
                    <div className="flex justify-between gap-2"><span className="font-semibold">{g.name}</span>
                      <span className="font-semibold whitespace-nowrap">{g.fee ? `$${Number(g.fee).toLocaleString()}` : 'Free'}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{[bornRange(g), g.full ? 'Full — waitlist' : (g.spots_left != null ? `${g.spots_left} spots left` : '')].filter(Boolean).join(' · ')}</div>
                    {g.description && <div className="text-xs text-slate-500 mt-1 whitespace-pre-line">{g.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {msg && (
            <div className="border-t dark:border-slate-700 px-5 sm:px-6 py-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">From the club</div>
              <div className={`relative ${long && !showAll ? 'max-h-72 overflow-hidden' : ''}`}>
                <div className="text-[15px] leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_h4]:font-semibold [&_h4]:pt-1 [&_b]:font-semibold" data-testid="reg-welcome" dangerouslySetInnerHTML={{ __html: msg }} />
                {long && !showAll && <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white dark:from-slate-800" />}
              </div>
              {long && <button className="mt-2 text-sm font-medium underline" style={{ color: brand }} onClick={() => setShowAll(v => !v)} data-testid="reg-read-more">{showAll ? 'Show less' : 'Read more'}</button>}
            </div>
          )}
          <div className="px-5 sm:px-6 pb-5 text-sm text-slate-500 hidden sm:block">
            {!me && !preview && <>Have a family login? <button className="underline" onClick={() => go('auth')} data-testid="reg-signin-link">Sign in</button> — or just start, no login needed.</>}
          </div>
        </div>
        <div className="sm:hidden sticky bottom-3 z-10 space-y-2">
          {startBtn}
          {!me && !preview && <p className="text-center text-xs text-slate-500 bg-slate-50/90 dark:bg-slate-900/90 rounded py-1">No login needed · <button className="underline" onClick={() => go('auth')}>Sign in</button></p>}
        </div>
      </>)
    );
  }

  if (step === 'auth') return shell(<><AuthCard orgId={orgId} form={form} brand={brand} inp={inp} primaryBtn={primaryBtn} note={authNote} onDone={u => { setAuthNote(''); afterSignIn(u); }} onBack={() => { setAuthNote(''); go(returnStep || 'landing'); setReturnStep(''); }} /></>);

  if (step === 'resume') {
    return (
      shell(<>
        <div className="rounded-xl border p-5 bg-white dark:bg-slate-800 dark:border-slate-700 space-y-3" data-testid="reg-resume">
          <h2 className="text-lg font-bold">Welcome back</h2>
          <p className="text-sm text-slate-600 dark:text-slate-300">You started this registration{draft?.player_names?.length ? ` for ${draft.player_names.join(', ')}` : ''} on {fmtDate(draft?.created_at)}. Pick up where you left off?</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button className={primaryBtn} style={{ background: brand }} onClick={resume} data-testid="reg-resume-continue">Continue <ChevronRight className="h-4 w-4" /></button>
            <button className={`${ghostBtn} border dark:border-slate-600`} onClick={startOver} data-testid="reg-resume-over">Start over</button>
          </div>
        </div>
      </>)
    );
  }

  if (step === 'done' && result) {
    return (
      shell(<>
        <div className="text-center p-8 rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700" data-testid="registration-done">
          <CheckCircle2 className="h-14 w-14 mx-auto text-green-500 mb-3" />
          <h3 className="text-xl font-bold">Registration submitted</h3>
          <p className="text-slate-500">{result.players_registered} {form.staff_only ? 'registration' : 'player(s)'} registered.</p>
          {result.pay_pending && <p className="mt-2 text-sm rounded-lg border px-3 py-2 bg-amber-50 border-amber-200 text-amber-800" data-testid="reg-pay-pending">Your registration is in. Online payment couldn't start just now — you'll find the balance under My invoices, and the club will follow up.</p>}
          {form.confirmation_message && <div className={`mt-3 text-sm text-left space-y-2 ${msgCls(form.confirmation_message)}`} dangerouslySetInnerHTML={{ __html: form.confirmation_message }} />}
          {(result.registrations || []).map(r => (
            <div key={r.submission_id} className="flex justify-between p-2 mt-2 rounded bg-slate-50 dark:bg-slate-700/40 text-sm">
              <span>{r.player_name}</span><span>{r.team} · {r.placement}</span>
            </div>
          ))}
        </div>
      </>)
    );
  }

  const fin = form.financials || {};
  const groupById = (id) => (form.groups || []).find(g => g.id === id) || {};
  const playerFee = (p) => {
    const g = groupById(p.group_id); let base = Number(g.fee || 0);
    allQuestions().filter(q => q.scope !== 'family').forEach(q => {
      const rf = q.response_fees || {}; const v = (p.answers || {})[q.id];
      (Array.isArray(v) ? v : [v]).forEach(x => {
        const f = x ? rf[x] : null; if (!f) return;
        if (typeof f === 'object' && 'min' in f) base += Number(p.answers[`${q.id}__amount`] || 0);
        else if (typeof f === 'object') base += Number(f.amount || 0) * Number(p.answers[`${q.id}__qty`] ?? 1);
        else base += Number(f);
      });
    });
    return base;
  };
  const formFee = Number(fin.form_fee || 0);
  const subtotal = round2(players.reduce((s, p) => s + playerFee(p), 0) + (players.length ? formFee : 0));
  const applyCoupon = async () => {
    if (preview) { setCouponMsg('Preview — codes are checked when a family checks out'); return; }
    if (!coupon.trim()) { setAppliedDiscount(0); setCouponMsg(''); return; }
    try {
      const { data } = await axios.post(`${API_URL}/api/reg-submissions/${orgId}/public/forms/${form.id}/validate-coupon`, { code: coupon.trim() }, regCfg());
      if (!data.valid) { setAppliedDiscount(0); setCouponMsg('Invalid code'); return; }
      const amt = data.type === 'pct' ? subtotal * Number(data.value) / 100 : Number(data.value);
      setAppliedDiscount(Math.min(round2(amt), subtotal)); setCouponMsg(`${data.description || 'Applied'} (confirmed at checkout)`);
    } catch { setAppliedDiscount(0); setCouponMsg('Invalid code'); }
  };
  // Online fee shown only if the form passes it through AND the rate is configured
  // (else "calculated at checkout" — never a hardcoded rate).
  const onlineFee = fin.pass_online_fee
    ? ((fin.online_fee_rate != null || fin.online_fee_pct != null || fin.online_fee_flat != null)
        ? round2((subtotal - appliedDiscount) * Number(fin.online_fee_rate ?? fin.online_fee_pct ?? 0) / 100 + Number(fin.online_fee_flat || 0))
        : null)
    : 0;
  const dueToday = round2(Math.max(subtotal - appliedDiscount, 0) + (onlineFee || 0));
  // Money due at sign-up → a family login is needed (AJ 2026-10-15); otherwise guests are fine.
  const payNow = (fin.charge_when || 'when_selected') !== 'when_selected' && dueToday > 0;
  const planSchedule = (pl) => {
    const today = new Date();
    if (Array.isArray(pl.installments) && pl.installments.length) {
      return pl.installments.map((s, i) => ({
        label: `Installment ${i + 1}${s.due_date ? ` (${fmtDate(s.due_date)})` : ''}`,
        amount: round2(Number(s.amount || 0)), passed: s.due_date ? new Date(s.due_date) < today : false,
      }));
    }
    const n = Number(pl.count ?? (typeof pl.installments === 'number' ? pl.installments : 0)); if (!n) return [];
    const dep = Number(pl.deposit || 0);
    const per = round2(Math.max(dueToday - dep, 0) / n);
    const dates = pl.due_dates || [];
    return Array.from({ length: n }).map((_, i) => {
      const d = dep > 0 ? dates[i] : (i > 0 ? dates[i - 1] : null);
      return { label: `Installment ${i + 1}${d ? ` (${fmtDate(d)})` : (pl.interval_days ? ` (+${pl.interval_days * (dep > 0 ? i + 1 : i)} days)` : '')}`, amount: per, passed: d ? new Date(d) < today : false };
    });
  };

  const groups = form.groups || [];
  const p = players[cur] || { answers: {} };
  const card = 'rounded-xl border p-4 sm:p-5 bg-white dark:bg-slate-800 dark:border-slate-700 space-y-4';
  return (
    shell(<>
      {progress()}
      {laterMsg && <div className="rounded-lg border px-3 py-2 text-sm bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300" data-testid="reg-later-msg">{laterMsg}</div>}
      {laterAsk && (
        <div className="rounded-lg border p-3 space-y-2 bg-white dark:bg-slate-800 dark:border-slate-700" data-testid="reg-later-ask">
          <p className="text-sm">Where should we send the link back to this registration?</p>
          <div className="flex gap-2"><input type="email" placeholder="Email" className={inp(false)} value={laterEmail} onChange={e => setLaterEmail(e.target.value)} data-testid="reg-later-email" />
            <button className={primaryBtn} style={{ background: brand }} onClick={() => finishLater(laterEmail.trim())} data-testid="reg-later-send">Send</button></div>
        </div>
      )}

      {step === 'family' && (
        <div className={card} data-testid="registration-form">
          <h3 className="font-bold flex items-center gap-2"><Users className="h-5 w-5" style={{ color: brand }} />Parents / guardians</h3>
          {[['father', 'Parent / guardian 1'], ['mother', 'Parent / guardian 2']].map(([k, lbl]) => (
            <div key={k} className="space-y-2">
              <div className="text-xs font-semibold text-slate-500">{lbl}{k === 'mother' ? ' (optional)' : ''}</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input placeholder="Full name" autoComplete={k === 'father' ? 'name' : 'off'} className={inp(errors.parent && k === 'father' && !family.father?.name)} value={family[k]?.name || ''} data-testid={`fam-${k}-name`} onChange={e => setFam(k, { name: e.target.value })} />
                <input placeholder="Mobile phone" type="tel" autoComplete={k === 'father' ? 'tel' : 'off'} className={inp(errors.parent && k === 'father' && !family.father?.phone)} value={family[k]?.phone || ''} data-testid={`fam-${k}-phone`} onChange={e => setFam(k, { phone: e.target.value })} />
                <input placeholder="Email" type="email" autoComplete={k === 'father' ? 'email' : 'off'} className={inp(errors.parent && k === 'father' && !family.father?.email)} value={family[k]?.email || ''} data-testid={`fam-${k}-email`} onChange={e => setFam(k, { email: e.target.value })} />
              </div>
            </div>
          ))}
          {errMsg('parent')}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-500">Home address</div>
            <input placeholder="Street" autoComplete="street-address" className={inp(false)} value={family.address?.street || ''} data-testid="fam-address" onChange={e => setFam('address', { street: e.target.value })} />
            <div className="grid grid-cols-6 gap-2">
              <input placeholder="City" autoComplete="address-level2" className={`${inp(false)} col-span-6 sm:col-span-3`} value={family.address?.city || ''} onChange={e => setFam('address', { city: e.target.value })} />
              <input placeholder="State" autoComplete="address-level1" className={`${inp(false)} col-span-2 sm:col-span-1`} value={family.address?.state || ''} onChange={e => setFam('address', { state: e.target.value })} />
              <input placeholder="ZIP" inputMode="numeric" autoComplete="postal-code" className={`${inp(false)} col-span-4 sm:col-span-2`} value={family.address?.zip || ''} onChange={e => setFam('address', { zip: e.target.value })} />
            </div>
          </div>
          {withSections(allQuestions().filter(q => q.scope === 'family'), family.answers || {}, (qid, v) => setFamily(f => ({ ...f, answers: { ...(f.answers || {}), [qid]: v } })))}
          <div className="flex items-center justify-between gap-2 pt-3 border-t dark:border-slate-700">
            <div className="flex items-center gap-2 text-sm">Players
              <button className="h-10 w-10 border rounded-lg dark:border-slate-600" aria-label="One fewer player" onClick={() => setPlayers(x => x.length > 1 ? x.slice(0, -1) : x)}>−</button>
              <span data-testid="player-count" className="w-5 text-center font-semibold">{players.length}</span>
              <button className="h-10 w-10 border rounded-lg dark:border-slate-600" aria-label="Add a player" data-testid="add-player" onClick={() => setPlayers(x => [...x, { answers: {} }])}>+</button></div>
            <button className={primaryBtn} style={{ background: brand }} onClick={() => guard(checkFamily(), () => { setCur(0); go('players'); })} data-testid="to-players">Next <ChevronRight className="h-4 w-4" /></button>
          </div>
          {saveBar()}
        </div>
      )}

      {step === 'players' && (
        <div className={card} data-testid="registration-form">
          <h3 className="font-bold flex items-center gap-2"><User className="h-5 w-5" style={{ color: brand }} />{form.staff_only ? 'Your details' : `Player ${cur + 1} of ${players.length}`}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><input placeholder="First name" className={inp(errors.first_name)} value={p.first_name || ''} data-testid="p-first" onChange={e => setP({ first_name: e.target.value })} />{errMsg('first_name')}</div>
            <div><input placeholder="Last name" className={inp(errors.last_name)} value={p.last_name || ''} data-testid="p-last" onChange={e => setP({ last_name: e.target.value })} />{errMsg('last_name')}</div>
            <div><label className="text-xs text-slate-500">Date of birth</label><input type="date" className={inp(errors.birthday)} value={p.birthday || ''} data-testid="p-dob" onChange={e => setP({ birthday: e.target.value })} />{errMsg('birthday')}</div>
            <div><label className="text-xs text-slate-500">Gender</label><select className={inp(errors.gender)} value={p.gender || ''} data-testid="p-gender" onChange={e => setP({ gender: e.target.value })}><option value="">Choose…</option><option>Male</option><option>Female</option></select>{errMsg('gender')}</div>
            {form.staff_only && <>
              <div><input type="email" placeholder="Email" autoComplete="email" className={inp(errors.email)} value={p.email || ''} data-testid="p-email" onChange={e => setP({ email: e.target.value })} />{errMsg('email')}</div>
              <div><input type="tel" placeholder="Mobile phone" autoComplete="tel" className={inp(false)} value={p.mobile || ''} data-testid="p-mobile" onChange={e => setP({ mobile: e.target.value })} /></div>
            </>}
          </div>
          {groups.some(g => g.enabled !== false) && (
            <div><label className="text-xs text-slate-500">Registering for</label>
              <select className={inp(errors.group_id)} value={p.group_id || ''} data-testid="p-group" onChange={e => setP({ group_id: e.target.value })}>
                <option value="">Choose a group / team…</option>
                {groups.filter(g => g.enabled !== false).map(g => <option key={g.id} value={g.id}>{g.name}{g.fee ? ` — $${Number(g.fee).toLocaleString()}` : ''}{g.full ? ' — Full (waitlist)' : (g.spots_left != null ? ` — ${g.spots_left} spots left` : '')}</option>)}
              </select>{errMsg('group_id')}</div>
          )}
          {form.staff_only && withSections(allQuestions().filter(q => q.scope === 'family'), family.answers || {}, (qid, v) => setFamily(f => ({ ...f, answers: { ...(f.answers || {}), [qid]: v } })))}
          {withSections(allQuestions().filter(q => q.scope !== 'family'), p.answers || {}, setPA, [p.first_name, p.last_name].filter(Boolean).join(' '))}
          {(form.agreements || []).filter(a => a.enabled !== false).map(a => {
            const k = `agreement_${a.id}`; const val = (p.answers || {})[k] || '';
            return (
              <div key={a.id} className={`rounded-lg border p-3 text-sm dark:border-slate-600 ${errors[k] ? 'border-red-500' : ''}`}>
                <div className="font-semibold">{a.title}</div>
                <div className="text-xs text-slate-500 my-1 max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: a.content || '' }} />
                {a.library_url
                  ? <a href={a.library_pdf_path ? `${API_URL}${a.library_pdf_path}` : a.library_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: brand }} data-testid={`agreement-doc-${a.id}`}>View / print: {a.library_title || 'document'}</a>
                  : a.attachment_url && <a href={a.attachment_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: brand }}>View attachment</a>}
                {(a.signature_type || 'accept') === 'accept' ? (
                  <label className="flex items-center gap-3 mt-2 min-h-[40px]"><input type="checkbox" className="h-5 w-5" checked={val === 'accepted'} data-testid={`agree-${a.id}`} onChange={e => setPA(k, e.target.checked ? 'accepted' : '')} /> I agree</label>
                ) : (
                  <input placeholder={a.signature_type === 'full_name' ? 'Type your full name to sign' : 'Type your initials to sign'} className={`${inp(errors[k])} mt-2`} value={val} data-testid={`agree-${a.id}`} onChange={e => setPA(k, e.target.value)} />
                )}
                {errMsg(k)}
              </div>
            );
          })}
          <div className="flex justify-between gap-2 pt-3 border-t dark:border-slate-700">
            <button className={ghostBtn} onClick={() => (cur > 0 ? (setCur(cur - 1), setErrors({})) : form.staff_only ? go('landing') : go('family'))} data-testid="p-back"><ChevronLeft className="h-4 w-4" />Back</button>
            {cur < players.length - 1
              ? <button className={primaryBtn} style={{ background: brand }} onClick={() => guard(checkPlayer(p), () => { setCur(cur + 1); setErrors({}); topRef.current?.scrollIntoView({ behavior: 'smooth' }); })} data-testid="p-next">Next player <ChevronRight className="h-4 w-4" /></button>
              : <button className={primaryBtn} style={{ background: brand }} onClick={() => guard({ ...(form.staff_only ? checkFamily() : {}), ...checkPlayer(p) }, () => go('review'))} data-testid="to-review">Review <ChevronRight className="h-4 w-4" /></button>}
          </div>
          {saveBar()}
        </div>
      )}

      {step === 'review' && (
        <div className={card} data-testid="reg-review">
          <h3 className="font-bold">Review &amp; payment</h3>
          {(fin.charge_when || 'when_selected') === 'when_selected' && subtotal > 0 && <p className="text-sm rounded-lg px-3 py-2 bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-300">Nothing is charged now. If your player is selected, we'll email you a link to pay.</p>}
          <div className="space-y-1 text-sm">
            {players.map((pl, i) => { const g = groupById(pl.group_id); return (
              <div key={i} className="flex justify-between gap-2"><span className="min-w-0 truncate">{pl.first_name} {pl.last_name} — {g.name || 'No group'}</span><span>${playerFee(pl).toFixed(2)}</span></div>
            ); })}
            {formFee > 0 && <div className="flex justify-between"><span>Form fee (once per family)</span><span>${formFee.toFixed(2)}</span></div>}
            <div className="flex justify-between border-t pt-1 dark:border-slate-700"><span>Subtotal</span><span data-testid="review-subtotal">${subtotal.toFixed(2)}</span></div>
            {appliedDiscount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-${appliedDiscount.toFixed(2)}</span></div>}
            {fin.pass_online_fee && <div className="flex justify-between text-slate-500"><span>Online processing fee</span><span>{onlineFee != null ? `$${onlineFee.toFixed(2)}` : 'calculated at checkout'}</span></div>}
            <div className="flex justify-between font-bold border-t pt-1 dark:border-slate-700"><span>{(fin.charge_when || 'when_selected') === 'when_selected' ? 'Due if selected' : 'Total due today'}</span><span data-testid="review-total">${dueToday.toFixed(2)}</span></div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input placeholder="Coupon code" value={coupon} onChange={e => setCoupon(e.target.value)} className={`${inp(false)} flex-1 min-w-[140px]`} data-testid="coupon-input" />
            <button className="px-4 h-11 border rounded-lg text-sm dark:border-slate-600" onClick={applyCoupon} data-testid="coupon-apply">Apply</button>
            {couponMsg && <span className="text-xs w-full">{couponMsg}</span>}
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase text-slate-400">Payment option</div>
            <label className="flex items-center gap-3 text-sm min-h-[40px]"><input type="radio" className="h-5 w-5" name="pay" checked={payOption === 'full'} onChange={() => setPayOption('full')} data-testid="pay-full" /> Pay in full — ${dueToday.toFixed(2)}</label>
            {(fin.installment_plans || []).filter(pl => pl.enabled !== false).map(pl => (
              <label key={pl.id} className="block text-sm border rounded-lg p-3 dark:border-slate-600" data-testid={`pay-plan-${pl.id}`}>
                <input type="radio" name="pay" className="h-5 w-5 mr-2 align-middle" checked={payOption === pl.id} onChange={() => setPayOption(pl.id)} />{pl.name}
                <div className="text-xs text-slate-500 ml-7">
                  {pl.deposit != null && <div>Deposit today: ${Number(pl.deposit).toFixed(2)}</div>}
                  {planSchedule(pl).map((s, idx) => <div key={idx}>{s.label}: ${s.amount.toFixed(2)}{s.passed ? ' — collected at checkout' : ''}</div>)}
                  <div className="italic">If a date has passed, that amount will be collected at checkout.</div>
                </div>
              </label>
            ))}
            {fin.enable_cash && <label className="flex items-center gap-3 text-sm min-h-[40px]"><input type="radio" className="h-5 w-5" name="pay" checked={payOption === 'cash'} onChange={() => setPayOption('cash')} data-testid="pay-cash" /> Pay by cash or check (no online fee)</label>}
          </div>
          {submitErr && <div className="flex items-start gap-2 text-sm text-red-600" data-testid="reg-submit-err"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{submitErr}</div>}
          <div className="flex justify-between gap-2 pt-3 border-t dark:border-slate-700">
            <button className={ghostBtn} onClick={() => go('players')} data-testid="review-back"><ChevronLeft className="h-4 w-4" />Back</button>
            {payNow && !me && !preview ? (
              <button className={`${primaryBtn} bg-green-600`} onClick={() => { setAuthNote('This registration takes a payment now, so it needs a family login. Your answers are kept.'); setReturnStep('review'); go('auth'); }} data-testid="submit-login-to-pay">Sign in to pay <ChevronRight className="h-4 w-4" /></button>
            ) : (
            <button className={`${primaryBtn} bg-green-600`} disabled={submitting}
              onClick={() => submit({ option: payOption, coupon: coupon || null, discount: appliedDiscount, online_fee: onlineFee, subtotal, total_due: dueToday, method: payOption === 'cash' ? 'cash' : 'online' })}
              data-testid="submit-registration">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (payNow && payOption !== 'cash' ? 'Submit & pay' : 'Submit registration')}</button>
            )}
          </div>
          {saveBar()}
        </div>
      )}
    </>)
  );
}

// Sign in or create a family login — right on the registration page.
function AuthCard({ orgId, form, brand, inp, primaryBtn, note, onDone, onBack }) {
  const [mode, setMode] = useState('new');
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [codeFor, setCodeFor] = useState('');   // email the 6-digit code went to
  const [code, setCode] = useState('');
  const set = (k) => (e) => setV(x => ({ ...x, [k]: e.target.value }));
  const sendCode = async () => {
    const { data } = await axios.post(`${FAM}/${orgId}/forms/${form.id}/account`, v, { withCredentials: true });
    setCodeFor(data.email); setCode('');
  };
  const resend = async () => {
    setErr('');
    try { await sendCode(); setErr('New code sent.'); } catch (ex) { setErr(ex?.response?.data?.detail || 'Could not send a new code'); }
  };
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      if (mode === 'new' && codeFor) {
        const { data } = await axios.post(`${FAM}/${orgId}/forms/${form.id}/account/verify`, { email: codeFor, code }, { withCredentials: true });
        onDone(data.user);
      } else if (mode === 'new') {
        await sendCode();
      } else {
        const { data } = await axios.post(`${API_URL}/api/auth/login`, { email: v.email, password: v.password }, { withCredentials: true });
        onDone(data);
      }
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Check the details and try again');
      if (ex?.response?.status === 409) setMode('in');
    } finally { setBusy(false); }
  };
  const tab = (k, l) => (
    <button type="button" onClick={() => { setMode(k); setErr(''); setCodeFor(''); }} className={`flex-1 h-10 rounded-lg text-sm font-medium ${mode === k ? 'text-white' : 'border dark:border-slate-600'}`} style={mode === k ? { background: brand } : {}} data-testid={`reg-auth-${k}`}>{l}</button>
  );
  return (
    <form onSubmit={go} className="rounded-xl border p-5 bg-white dark:bg-slate-800 dark:border-slate-700 space-y-3" data-testid="reg-auth">
      <div>
        <h2 className="text-lg font-bold">Your family login</h2>
        <p className="text-sm text-slate-500">{note || 'Sign in to use your family details, or create a login for your family.'}</p>
      </div>
      <div className="flex gap-2">{tab('new', "I'm new here")}{tab('in', 'I have a login')}</div>
      {mode === 'new' && codeFor ? (
        <div className="space-y-2" data-testid="reg-auth-code">
          <p className="text-sm">We emailed a 6-digit code to <b>{codeFor}</b>. Enter it to confirm it's your email.</p>
          <input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" className={`${inp(false)} tracking-widest text-center`} value={code} onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))} data-testid="reg-auth-code-input" />
          <div className="flex gap-4 text-xs text-slate-500">
            <button type="button" className="underline" onClick={resend} data-testid="reg-auth-resend">Send a new code</button>
            <button type="button" className="underline" onClick={() => { setCodeFor(''); setErr(''); }}>Change my details</button>
          </div>
        </div>
      ) : (<>
      {mode === 'new' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input required placeholder="First name" autoComplete="given-name" className={inp(false)} onChange={set('first_name')} data-testid="reg-auth-first" />
          <input required placeholder="Last name" autoComplete="family-name" className={inp(false)} onChange={set('last_name')} data-testid="reg-auth-last" />
          <input placeholder="Mobile phone" type="tel" autoComplete="tel" className={`${inp(false)} sm:col-span-2`} onChange={set('phone')} data-testid="reg-auth-phone" />
        </div>
      )}
      <input required type="email" placeholder="Email" autoComplete="email" className={inp(false)} onChange={set('email')} data-testid="reg-auth-email" />
      <input required type="password" placeholder={mode === 'new' ? 'Create a password' : 'Password'} autoComplete={mode === 'new' ? 'new-password' : 'current-password'} className={inp(false)} onChange={set('password')} data-testid="reg-auth-password" />
      {mode === 'new' && <p className="text-xs text-slate-500">At least 8 characters, with an uppercase letter, a lowercase letter and a number. We'll email you a code to confirm your address.</p>}
      </>)}
      {err && <p className="text-sm text-red-600" data-testid="reg-auth-err">{err}</p>}
      <div className="flex items-center justify-between gap-2">
        <button type="button" className="text-sm text-slate-500 h-11 px-2" onClick={onBack}><ChevronLeft className="h-4 w-4 inline" />Back</button>
        <div className="flex items-center gap-3">
          {mode === 'in' && <a href="/login" className="text-xs underline text-slate-500">Forgot password?</a>}
          <button className={primaryBtn} style={{ background: brand }} disabled={busy} data-testid="reg-auth-go">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'new' ? (codeFor ? 'Confirm & continue' : 'Email me a code') : 'Sign in & continue'}</button>
        </div>
      </div>
    </form>
  );
}
